import Phaser from 'phaser';
import {
  ComponentDef,
  ComponentType,
  ComponentCategory,
  ComponentShape,
  COMPONENT_DEFS,
  getEvolutionMultiplier,
} from '@/components/ComponentDefs';
import { EVOLUTION_MAP } from '@/config/ComponentEvolution';
import { EventBus, GameEvents } from '@/events/EventBus';
import { SPEED_REFERENCE_MASS, COMPONENT_SPACING, TILE_SIZE, ROBOT_TILE_SCALE, TileType, TERRAIN_MODIFIERS, TerrainModifier, EnemyWeaponType } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { createComponentVisual } from '@/systems/ComponentIcons';
import { getOverride } from '@/config/ComponentOverrides';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { SoundManager } from '@/systems/SoundManager';
import { calculateStability, stabilitySpeedMultiplier, type StabilityResult } from '@/systems/StabilitySystem';
import { makeRenderTexture } from '@/utils/PhaserHelpers';

// ── Extracted modules ──
import type { RobotHost } from '@/entities/robot/RobotHost';
import { RobotDamageSystem } from '@/entities/robot/RobotDamageSystem';
import { TopplePhysics } from '@/entities/robot/TopplePhysics';
import { RobotFX } from '@/entities/robot/RobotFX';
import { RobotBarRenderer } from '@/entities/robot/RobotBarRenderer';

/** Popup font size scaled by damage/value magnitude and GOD.popupSizeMult. */
export function popupFontSize(value: number): number {
  let base: number;
  if (value >= 100) base = 28;
  else if (value >= 50) base = 22;
  else if (value >= 20) base = 17;
  else if (value >= 10) base = 14;
  else base = 11;
  return Math.round(base * GOD.popupSizeMult);
}

// ── Robot state enum ──
export enum RobotState {
  IDLE = 'idle',
  ATTACKING = 'attacking',
  MOVING = 'moving',
  RETREATING = 'retreating',
  SUPPORTING = 'supporting',
  FALLEN = 'fallen',
  DESTROYED = 'destroyed',
}

// ── Installed component instance ──
export interface InstalledComponent {
  def: ComponentDef;
  localX: number;  // offset from robot center
  localY: number;
  rotation: number; // radians, 0 = default orientation
  flipX: boolean;   // horizontal mirror
  flipY: boolean;   // vertical mirror
  graphic?: Phaser.GameObjects.Graphics | Phaser.GameObjects.Shape | Phaser.GameObjects.Sprite;
  crackOverlay?: Phaser.GameObjects.Graphics; // crack lines drawn over the component
  crackLevel: number; // 0 = none, 1 = light, 2 = heavy
  // Per-component durability
  compMaxHp: number;   // max HP for this component instance
  compCurrentHp: number; // current HP (0 = broken)
  broken: boolean;       // true when compCurrentHp reaches 0
  ejected: boolean;      // true if ejected on break — permanently lost, cannot be repaired
  exposed: boolean;      // true if on the outer edge of the chassis (can be hit directly)
  tween?: Phaser.Tweens.Tween; // stored reference to avoid getTweensOf() lookups
  // Armor shield (blue protection layer)
  shieldMax: number;     // max shield HP (0 = no shield)
  shieldCurrent: number; // current shield HP
  evoLevel: number;      // evolution tier of this specific instance (Pattern B)
}

// ── Evolution: create an evolved copy of ComponentDef with scaled stats ──
// Scales: hp, energy (capacity), damage, fireRate, weaponRange, sensorRange, repairRate, rechargeRate
// Does NOT scale: force, cost (would break balance — except flight components)
// Mass scales with tier tradeoff: +GOD.tierMassMult per tier (Pattern B)
export function evolveComponentDef(baseDef: ComponentDef, level: number): ComponentDef {
  if (level === 0) return baseDef; // no evolution → use original (no copy needed)
  // Use per-component multiplier from evolution chain if defined, else generic
  const chain = EVOLUTION_MAP.get(baseDef.type);
  const stage = chain?.stages.find(s => s.level === level);
  const m = stage?.statMultiplier ?? getEvolutionMultiplier(level);
  // Fire rate scales at half rate to avoid quadratic DPS growth (DPS = dmg × fireRate)
  // m=1.3 → fm=1.15, m=1.6 → fm=1.30
  const fm = 1 + (m - 1) * GOD.evoFireRateScale;
  // Support modules get special scaling: range/level, drain reduction/level
  const isSupport = baseDef.category === ComponentCategory.SUPPORT;
  const supportRangeMult = isSupport ? Math.pow(GOD.evoSupportRangeBase, level) : 1;
  const drainReduction = isSupport ? Math.pow(GOD.evoSupportDrainBase, level) : 1;

  // Flight components (Wing, Turbine): faster + efficient but FRAGILE
  // force ×m, energy drain /m, but hp /m and mass /m — high risk / high reward
  const isFlight = baseDef.type === ComponentType.WING || baseDef.type === ComponentType.TURBINE;

  const evolved: ComponentDef = {
    ...baseDef,
    hp: isFlight ? Math.round(baseDef.hp / m) : Math.round(baseDef.hp * m),
    energy: baseDef.energy > 0
      ? Math.round(baseDef.energy * m)                  // scale capacity up
      : isFlight
        ? +(baseDef.energy / m).toFixed(1)              // flight: reduce drain (÷m)
        : isSupport
          ? +(baseDef.energy * drainReduction).toFixed(1) // support: reduce drain (×0.85^lvl)
          : +(baseDef.energy * (1 + GOD.tierEnergyDrainMult * level)).toFixed(1), // others: +drain per tier
    damage: +(baseDef.damage * m).toFixed(1),
    fireRate: +(baseDef.fireRate * fm).toFixed(2),
    weaponRange: Math.round(baseDef.weaponRange * m),
    sensorRange: Math.round(baseDef.sensorRange * m),
    repairRate: +(baseDef.repairRate * m).toFixed(1),
    rechargeRate: +(baseDef.rechargeRate * m).toFixed(1),
    supportRange: Math.round(baseDef.supportRange * supportRangeMult),
    explosionDamage: Math.round(baseDef.explosionDamage * m),
    explosionRadius: Math.round(baseDef.explosionRadius * m),
    projectileSpeed: Math.round(baseDef.projectileSpeed * Math.pow(1.3, level)),
    force: isFlight ? Math.round(baseDef.force * m) : baseDef.force,
    mass: isFlight ? Math.round(baseDef.mass / m) : Math.round(baseDef.mass * (1 + GOD.tierMassMult * level)),
  };

  // Apply per-type per-level overrides from Weapon Tuner / ComponentOverrides
  const ov = getOverride(baseDef.type, level);
  if (ov.damage !== undefined) evolved.damage = ov.damage;
  if (ov.fireRate !== undefined) evolved.fireRate = ov.fireRate;
  if (ov.weaponRange !== undefined) evolved.weaponRange = ov.weaponRange;
  if (ov.hp !== undefined) evolved.hp = ov.hp;
  if (ov.energy !== undefined) evolved.energy = ov.energy;
  if (ov.sensorRange !== undefined) evolved.sensorRange = ov.sensorRange;
  if (ov.repairRate !== undefined) evolved.repairRate = ov.repairRate;
  if (ov.rechargeRate !== undefined) evolved.rechargeRate = ov.rechargeRate;
  if (ov.supportRange !== undefined) evolved.supportRange = ov.supportRange;
  if (ov.explosionDamage !== undefined) evolved.explosionDamage = ov.explosionDamage;
  if (ov.explosionRadius !== undefined) evolved.explosionRadius = ov.explosionRadius;
  if (ov.force !== undefined) evolved.force = ov.force;
  if (ov.mass !== undefined) evolved.mass = ov.mass;
  if (ov.projectileSpeed !== undefined) evolved.projectileSpeed = ov.projectileSpeed;

  // Armor-specific per-level overrides (not scaled by statMultiplier)
  if (stage?.shieldHp !== undefined) evolved.shieldHp = stage.shieldHp;
  if (stage?.ricochetChance !== undefined) evolved.ricochetChance = stage.ricochetChance;
  if (stage?.ricochetBeamChance !== undefined) evolved.ricochetBeamChance = stage.ricochetBeamChance;

  return evolved;
}

// ── Robot class ──
export class Robot implements RobotHost {
  /** Pixels per component cell on screen — derived from ROBOT_TILE_SCALE × TILE_SIZE × GOD.robotScaleMult */
  static get SCALE_ROBOT(): number { return ROBOT_TILE_SCALE * TILE_SIZE * GOD.robotScaleMult; }

  public scene: Phaser.Scene;
  public id: string;

  // Position on map (pixel coords)
  public x: number;
  public y: number;

  // Components
  public components: InstalledComponent[] = [];

  // Computed stats (recalculated on assembly change)
  public totalMass: number = 0;
  public totalHp: number = 0;
  public currentHp: number = 0;
  public maxEnergy: number = 0;
  public currentEnergy: number = 0;
  /** Once true, totalHp and maxEnergy are frozen (breaking components won't reduce the max) */
  private statsLocked = false;
  public moveForce: number = 0;
  public speed: number = 0;
  /** Slow debuff: multiplier < 1 reduces speed, timer counts down */
  public slowFactor: number = 1;
  public slowTimer: number = 0;
  // Base stats before GOD multipliers (for live GOD updates without compounding)
  private baseTotalHp: number = 0;
  private baseMaxEnergy: number = 0;
  private baseSpeed: number = 0;
  public showSensorCircle: boolean = true;
  public energySaveMode: boolean = false; // hysteresis flag for energy conservation
  public sensorRange: number = 0;
  public hasDirectionComponent: boolean = false;
  public sensorHalfAngle: number = Math.PI; // default = full circle (radial)
  /** Per-sensor fixed directions (relative to heading). 1 sensor = sweep, 2+ = evenly spaced fixed cones. */
  public sensorDirections: number[] = [];
  public directionAccuracyBonus: number = 0; // 0-1 — reduces miss chance from instability
  public directionRangeMult: number = 1;     // sensor range multiplier from Direction component
  public stabLegsDamageReduction: number = 0; // 0-1 — % damage reduction from Stab Legs

  // ── Radar sweep (directional sensors rotate like sonar) ──
  public sweepAngle: number = 0;             // current sweep rotation (radians, increments each frame)
  public sweepSpeed: number = Math.PI / 3;   // radians/s — full revolution speed (default ~6s/rev)
  /** Enemies detected by last sweep pass — kept visible for SWEEP_MEMORY_DURATION seconds */
  public sweepMemory: Map<string, number> = new Map(); // enemyId → timestamp of last detection
  /** Lock-on target for radar — set by BehaviorEngine when enemies are detected, null = free sweep */
  public sweepLockTarget: { x: number; y: number } | null = null;
  // Stability / balance
  public stability: StabilityResult = { score: 1, comX: 0, comY: 0, tiltAngle: 0, supportCount: 0, label: 'N/A', foundationFactor: 1.0, foundationPenaltyTypes: [] };
  public isFallen: boolean = false;
  public fallDirection: number = 0; // +1 = fell right, -1 = fell left
  // Flying instability spin — flyers don't topple, they spin erratically
  public spinRate: number = 0; // rad/s, driven by RobotPhysics for unstable flyers
  // Crash sequence state (flying robots that become very unstable)
  public isCrashing: boolean = false;

  // ── Terrain modifier ──
  // Updated each frame by RobotPhysics based on current tile under robot
  public terrainMod: TerrainModifier | null = null;
  private lastTerrainTile: TileType | null = null;
  private terrainLabel?: Phaser.GameObjects.Text;

  // ── Jumper state ──
  // Bottom jumpers give hop movement; side jumpers push enemies/allies
  public jumpPhase: number = 0;         // 0..1 — 0=grounded, 0→0.5=compression, 0.5→1=airborne
  public jumpTimer: number = 0;         // seconds into current jump cycle
  public jumpCooldown: number = 0;      // seconds until next side-push
  public isHopping: boolean = false;    // true = robot uses hop locomotion (has bottom jumper)
  private jumpYOffset: number = 0;      // visual Y offset for hop arc

  // ── Camouflage state ──
  public isCamoActive: boolean = false;   // true = invisible to enemies (has energy + camo module)
  private camoAlpha: number = 1;          // blending alpha (1=visible, 0.15=cloaked)
  private camoTimer: number = 0;          // accumulator for shimmer effects

  // ── Speed trail state ──
  private trailTimer: number = 0;         // accumulator for trail spawn interval

  // Original design data (for editing)
  public designComponents: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[] = [];
  public designRules: import('@/behaviors/BehaviorDefs').BehaviorRule[] = [];
  public designCost: number = 0;
  public designName: string = '';

  // State
  public state: RobotState = RobotState.IDLE;
  public alive: boolean = true;
  public placed: boolean = false;

  // ── Physics body ──
  public vx: number = 0;               // current velocity px/s
  public vy: number = 0;
  public heading: number = Math.PI;     // facing left by default (radians)
  public buildHeading: number = Math.PI; // direction from DIRECTION component (workshop orientation)
  public faceTargetX: number = NaN;     // X of current target — used to flip fixed robots toward enemies
  public _miningSfxTimer: number = 0;   // throttle for mining SFX
  public moveIntentX: number = 0;       // desired movement direction (set by behavior)
  public moveIntentY: number = 0;
  public moveIntentSpeed: number = 0;   // desired speed (0 = wants to stop)
  public physicsRadius: number = 10;    // collision radius (px, set by normalizeSize)

  // Stun state (from powerful enemy hits)
  public stunTimer: number = 0;        // seconds remaining, 0 = not stunned
  public get isStunned(): boolean { return this.stunTimer > 0; }
  public stunStars: Phaser.GameObjects.Container | null = null;
  public stunStarsAngle: number = 0;

  // ── Poison DoT state (acid from Hive enemy) ──
  public poisonStacks: number = 0;        // current active stacks
  public poisonDps: number = 0;           // total DPS (sum of all stacks)
  public poisonTimer: number = 0;         // remaining duration (refreshed per stack)
  public poisonTickTimer: number = 0;     // tick interval counter
  private poisonTint: boolean = false;    // green tint flash

  // Cache for sensor cone to avoid redrawing every frame
  private lastConeX: number = -1;
  private lastConeY: number = -1;
  private lastConeHeading: number = -999;

  // Graphics container
  public container!: Phaser.GameObjects.Container;

  // Render scale applied to container so robot fits within TILE_SIZE * 0.5
  public renderScale: number = 1;
  // Whether the robot is flipped (facing left, toward enemies)
  public flipped: boolean = true;
  /** @internal Anti-flicker: timestamp of last direction flip */
  public lastFlipTime: number = 0;

  // Shadow (ground units = tight under body, flyers = offset + larger)
  private flyShadow?: Phaser.GameObjects.Sprite;
  private groundShadow?: Phaser.GameObjects.Sprite;
  /** Scale boost for flying robots (gives illusion of altitude) */
  private static readonly FLY_SCALE_BOOST = 1.15;
  /** Shadow offset from robot position (light from top-left) */
  private static readonly SHADOW_OFFSET_X = 12;
  private static readonly SHADOW_OFFSET_Y = 18;
  /** Altitude bobbing — periodic scale/shadow oscillation */
  private flyAltitudePhase = Math.random() * Math.PI * 2; // desync between robots
  private static readonly FLY_ALTITUDE_SPEED = 1.2;       // radians/s
  private static readonly FLY_ALTITUDE_SCALE_AMP = 0.06;  // ±6% scale
  private static readonly FLY_ALTITUDE_SHADOW_AMP = 6;    // ±6px extra shadow offset

  // Debug graphics
  private sensorCircle?: Phaser.GameObjects.Arc;
  private sensorConeGfx?: Phaser.GameObjects.Graphics;
  private lastConeSweep: number = -9999;

  private static nextId = 0;

  // ── Extracted module instances ──
  public readonly damageSystem: RobotDamageSystem;
  public readonly topplePhysics: TopplePhysics;
  public readonly fx: RobotFX;
  public readonly barRenderer: RobotBarRenderer;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    this.id = `robot_${Robot.nextId++}`;
    this.x = x;
    this.y = y;

    this.container = scene.add.container(x, y);
    this.container.setDepth(20);

    // Instantiate modules
    this.fx = new RobotFX(this);
    this.barRenderer = new RobotBarRenderer(this);
    this.damageSystem = new RobotDamageSystem(this, this.fx);
    this.topplePhysics = new TopplePhysics(this, this.damageSystem);

    this.createDebugOverlays();
  }

  // ── Assembly ──

  addComponent(type: ComponentType, localX: number = 0, localY: number = 0, rotation: number = 0, flipX = false, flipY = false, evoLevel = 0): InstalledComponent {
    const baseDef = COMPONENT_DEFS[type];
    const def = evolveComponentDef(baseDef, evoLevel);
    const shieldMax = Math.round((def.shieldHp ?? 0) * GOD.armorShieldMult);
    const installed: InstalledComponent = {
      def, localX, localY, rotation, flipX, flipY,
      compMaxHp: Math.round(def.hp * GOD.robotHpMult),
      compCurrentHp: Math.round(def.hp * GOD.robotHpMult),
      crackLevel: 0,
      broken: false, ejected: false, exposed: false,
      shieldMax,
      shieldCurrent: shieldMax,
      evoLevel,
    };

    // Create placeholder graphic
    installed.graphic = this.createComponentGraphic(def, localX, localY, rotation, flipX, flipY, evoLevel);
    (installed as any)._baseScaleX = installed.graphic.scaleX;
    (installed as any)._baseScaleY = installed.graphic.scaleY;
    this.container.add(installed.graphic);

    this.components.push(installed);
    this.recalcStats();

    // Bring debug overlays to top so they render above components
    this.bringOverlaysToTop();

    return installed;
  }

  removeComponent(index: number): void {
    const comp = this.components[index];
    if (!comp) return;
    if (comp.graphic) {
      comp.graphic.destroy();
    }
    this.components.splice(index, 1);
    this.recalcStats();
  }

  // ── Stats recalculation ──
  // Broken components still contribute mass but NOT stats (hp, energy, force, sensor, weapons)

  recalcStats(): void {
    this.totalMass = 0;
    // Only recalculate HP/energy totals if not locked (pre-deployment / workshop)
    const recalcHpEnergy = !this.statsLocked;
    if (recalcHpEnergy) {
      this.totalHp = 0;
      this.maxEnergy = 0;
    }
    this.moveForce = 0;
    this.sensorRange = 0;

    const sensorRanges: number[] = [];

    for (const comp of this.components) {
      const d = comp.def;
      // Mass always counts (broken parts are still dead weight)
      this.totalMass += d.mass;

      // Broken components contribute nothing else
      if (comp.broken) continue;

      if (recalcHpEnergy) {
        this.totalHp += Math.round(d.hp * GOD.robotHpMult);
        if (d.energy > 0) {
          this.maxEnergy += d.energy;
        }
      }

      if (d.category === ComponentCategory.MOVEMENT) {
        this.moveForce += d.force;
      }

      if (d.category === ComponentCategory.SENSOR) {
        sensorRanges.push(d.sensorRange);
      }
    }

    // Direction component: enables conical sensors with bonuses (only if not broken)
    const dirComp = this.components.find(c => c.def.type === ComponentType.DIRECTION && !c.broken);
    this.hasDirectionComponent = !!dirComp;
    if (dirComp) {
      const dirLevel = dirComp.evoLevel;
      // Cone widens with evolution: 60° → 90° → 120°
      const halfAngles = [Math.PI / 6, Math.PI / 4, Math.PI / 3]; // 30°, 45°, 60°
      this.sensorHalfAngle = halfAngles[Math.min(dirLevel, halfAngles.length - 1)];
      // Range multiplier: focused sensors see further (read from GOD)
      const rangeMults = [GOD.sensorRangeMult0, GOD.sensorRangeMult1, GOD.sensorRangeMult2];
      this.directionRangeMult = rangeMults[Math.min(dirLevel, rangeMults.length - 1)];
      // Accuracy bonus: reduces miss chance from instability (read from GOD)
      const accBonuses = [GOD.sensorAccBonus0, GOD.sensorAccBonus1, GOD.sensorAccBonus2];
      this.directionAccuracyBonus = accBonuses[Math.min(dirLevel, accBonuses.length - 1)];
      // Sweep speed: radar rotates like sonar — faster at higher levels
      const sweepRevSeconds = [6, 4, 2.5];
      const revTime = sweepRevSeconds[Math.min(dirLevel, sweepRevSeconds.length - 1)];
      this.sweepSpeed = (Math.PI * 2) / revTime;

      // Multi-sensor distribution: N sensors → evenly spaced fixed cones
      const sensorCount = sensorRanges.length;
      this.sensorDirections = [];
      if (sensorCount <= 1) {
        this.sensorDirections = [0];
      } else {
        for (let i = 0; i < sensorCount; i++) {
          this.sensorDirections.push(i * (Math.PI * 2 / sensorCount));
        }
        this.sweepSpeed = 0;
      }
    } else {
      this.sensorHalfAngle = Math.PI;
      this.directionRangeMult = 1;
      this.directionAccuracyBonus = 0;
      this.sweepSpeed = 0;
      this.sensorDirections = [];
    }

    // Stab Legs
    const stabLegs = this.components.find(c => c.def.type === ComponentType.STAB_LEGS && !c.broken);
    if (stabLegs) {
      this.directionAccuracyBonus = Math.min(1, this.directionAccuracyBonus + 0.3);
      this.stabLegsDamageReduction = 0.2;
    } else {
      this.stabLegsDamageReduction = 0;
    }

    // Jumper
    this.isHopping = this.hasBottomJumper() && !this.canFly();
    if (this.isHopping) {
      for (const j of this.getBottomJumpers()) {
        this.moveForce += j.def.force;
      }
    }

    // Sensor range: diminishing returns
    sensorRanges.sort((a, b) => b - a);
    for (let i = 0; i < sensorRanges.length; i++) {
      this.sensorRange += i === 0 ? sensorRanges[i] : sensorRanges[i] * 0.5;
    }
    this.sensorRange = Math.round(this.sensorRange * this.directionRangeMult);

    if (sensorRanges.length === 0 && this.components.some(c => c.def.category === ComponentCategory.SENSOR && !c.broken)) {
      this.sensorRange = TILE_SIZE;
    }

    if (this.isFallen) this.sensorRange = Math.round(this.sensorRange * 0.5);

    const rawSpeed = this.totalMass > 0
      ? (this.moveForce / this.totalMass) * SPEED_REFERENCE_MASS
      : 0;

    const compEntries = this.components
      .filter(c => !c.ejected)
      .map(c => ({ type: c.def.type, lx: c.localX, ly: c.localY, broken: c.broken }));
    const designedWithMovement = this.components.some(
      c => c.def.category === ComponentCategory.MOVEMENT,
    );
    this.stability = calculateStability(compEntries, designedWithMovement, this.buildHeading);

    this.speed = rawSpeed * stabilitySpeedMultiplier(this.stability.score);

    if (this.isFallen) {
      const angleFraction = this.topplePhysics.toppleMaxAngle / (Math.PI / 2);
      const dragMult = Math.max(0.05, 0.4 * (1 - angleFraction));
      this.speed *= dragMult;
    }

    if (this.container && !this.isFallen && !this.canFly()) {
      this.container.setRotation(this.stability.tiltAngle);
    } else if (this.container && this.canFly() && !this.isFallen) {
      this.container.setRotation(0);
    }

    if (recalcHpEnergy) {
      const hasSupport = this.components.some(c => !c.broken && c.def.category === ComponentCategory.SUPPORT);
      if (hasSupport) {
        this.totalHp = Math.round(this.totalHp * 0.5);
      }
    }

    if (recalcHpEnergy) {
      this.baseTotalHp = this.totalHp;
      this.baseMaxEnergy = this.maxEnergy;
    }
    this.baseSpeed = this.speed;

    if (recalcHpEnergy) {
      this.maxEnergy = Math.round(this.baseMaxEnergy * GOD.robotEnergyMult);
    }
    this.speed = this.baseSpeed * GOD.robotSpeedMult;

    if (this.terrainMod) {
      this.speed *= this.terrainMod.speedMult;
      this.sensorRange = Math.round(this.sensorRange * this.terrainMod.rangeMult);
    }
    if (this.slowTimer > 0) {
      this.speed *= this.slowFactor;
    }

    if (this.currentHp === 0 && this.totalHp > 0) {
      this.currentHp = this.totalHp;
    }
    if (this.currentEnergy === 0 && this.maxEnergy > 0) {
      this.currentEnergy = this.maxEnergy;
    }

    if (this.currentHp > this.totalHp) this.currentHp = this.totalHp;
    if (this.currentEnergy > this.maxEnergy) this.currentEnergy = this.maxEnergy;

    this.computeExposure();
    this.updateDebugOverlays();
  }

  // ── Exposure: components on the outer boundary of the chassis can be hit directly ──

  private computeExposure(): void {
    if (this.components.length === 0) return;

    const occupied = new Set<string>();
    for (const c of this.components) {
      occupied.add(`${c.localX},${c.localY}`);
    }

    const step = COMPONENT_SPACING;
    for (const comp of this.components) {
      const neighbors = [
        `${comp.localX - step},${comp.localY}`,
        `${comp.localX + step},${comp.localY}`,
        `${comp.localX},${comp.localY - step}`,
        `${comp.localX},${comp.localY + step}`,
      ];
      comp.exposed = neighbors.some(n => !occupied.has(n));
    }
  }

  resetHpEnergy(): void {
    this.currentHp = this.totalHp;
    this.currentEnergy = this.maxEnergy;
    this.statsLocked = true;
    this.updateDebugOverlays();

    if (!this.isFallen && !this.canFly() && this.stability.score < 0.3) {
      const fallDir = Math.sign(this.stability.tiltAngle)
        || Math.sign(this.stability.comX)
        || (Math.random() < 0.5 ? -1 : 1);
      this.topple(fallDir);
    }
  }

  // ── Energy management ──

  static readonly ENERGY_DRAIN_MULTIPLIER = 1;

  consumeEnergy(amount: number, isPassiveSupport = false): boolean {
    if (GOD.infiniteEnergy) return true;
    const supportPenalty = (!isPassiveSupport && this.isSupportRobot() && this.state !== RobotState.SUPPORTING) ? 2 : 1;
    const drain = amount * Robot.ENERGY_DRAIN_MULTIPLIER * GOD.robotEnergyDrainMult * supportPenalty;
    if (this.currentEnergy >= drain) {
      this.currentEnergy -= drain;
      return true;
    }
    return false;
  }

  hasEnergy(): boolean {
    return this.currentEnergy > 0;
  }

  // ── Damage delegation ──

  applySlow(factor: number, duration: number): void {
    if (this.slowTimer > 0 && this.slowFactor <= factor) return;
    this.slowFactor = factor;
    this.slowTimer = duration;
    this.recalcStats();
  }

  tickSlow(dt: number): void {
    if (this.slowTimer > 0) {
      this.slowTimer -= dt;
      if (this.slowTimer <= 0) {
        this.slowTimer = 0;
        this.slowFactor = 1;
        this.recalcStats();
      }
    }
  }

  takeDamage(amount: number, attackerX?: number, attackerY?: number): void {
    this.damageSystem.takeDamage(amount, attackerX, attackerY);
  }

  /** Apply stackable poison DoT from acid catapult. Refreshes duration, stacks DPS up to max. */
  applyPoison(duration: number, dps: number): void {
    if (!this.alive) return;
    const maxStacks = GOD.hivePoisonMaxStacks;
    if (this.poisonStacks < maxStacks) {
      this.poisonStacks++;
      this.poisonDps += dps;
    }
    // Refresh duration to max of current and new
    this.poisonTimer = Math.max(this.poisonTimer, duration);
  }

  /** Update poison DoT — called from RobotManager each frame */
  updatePoison(dt: number): void {
    if (this.poisonTimer <= 0 || !this.alive) return;

    this.poisonTimer -= dt;
    this.poisonTickTimer -= dt;

    // Green tint flash
    this.poisonTint = !this.poisonTint;
    if (this.poisonTint && this.container) {
      // Subtle green tint on all children
      this.container.iterate((child: Phaser.GameObjects.GameObject) => {
        if ((child as any).setTint) (child as any).setTint(0x88ff44);
      });
    }

    // Damage tick every 0.5s
    if (this.poisonTickTimer <= 0) {
      this.poisonTickTimer = 0.5;
      const tickDmg = this.poisonDps * 0.5; // damage per tick = DPS × tick interval
      this.takeDamage(tickDmg);

      // Green floating damage number
      if (this.container && this.scene) {
        const txt = this.scene.add.text(
          this.x + (Math.random() - 0.5) * 10,
          this.y - 15,
          `-${Math.round(tickDmg)}`,
          { fontFamily: 'monospace', fontSize: `${popupFontSize(tickDmg)}px`, color: '#44ff22', stroke: '#003300', strokeThickness: 2 },
        ).setOrigin(0.5).setDepth(50);
        this.scene.tweens.add({
          targets: txt, y: txt.y - 20, alpha: 0,
          duration: 800, onComplete: () => txt.destroy(),
        });
      }

      // Green drip particles
      if (this.scene) {
        for (let i = 0; i < 2; i++) {
          const drip = this.scene.add.circle(
            this.x + (Math.random() - 0.5) * 12,
            this.y + (Math.random() - 0.5) * 12,
            2, 0x66ff22, 0.7,
          ).setDepth(25);
          this.scene.tweens.add({
            targets: drip, alpha: 0, y: drip.y + 12,
            duration: 500 + Math.random() * 300, onComplete: () => drip.destroy(),
          });
        }
      }
    }

    // Poison expired
    if (this.poisonTimer <= 0) {
      this.poisonStacks = 0;
      this.poisonDps = 0;
      this.poisonTimer = 0;
      this.poisonTickTimer = 0;
      // Clear green tint
      if (this.container) {
        this.container.iterate((child: Phaser.GameObjects.GameObject) => {
          if ((child as any).clearTint) (child as any).clearTint();
        });
      }
    }
  }

  checkRicochet(weaponType: EnemyWeaponType, attackerX: number, attackerY: number):
    { normalX: number; normalY: number; impactX: number; impactY: number } | null {
    return this.damageSystem.checkRicochet(weaponType, attackerX, attackerY);
  }

  // ── Repair ──

  repairComponent(comp: InstalledComponent): void {
    if (!comp.broken) return;
    if (comp.ejected) return;
    comp.broken = false;
    comp.compCurrentHp = comp.compMaxHp;
    comp.crackLevel = 0;
    if (comp.graphic) {
      comp.graphic.setAlpha(1);
      if ((comp.graphic as any).clearTint) {
        (comp.graphic as any).clearTint();
      }
    }
    this.damageSystem.removeCracks(comp);
    this.fx.stopCompSmoke(comp);
    this.recalcStats();
  }

  getRepairCost(): number {
    if (this.totalHp <= 0 || this.currentHp >= this.totalHp) return 0;
    const damageRatio = 1 - this.currentHp / this.totalHp;
    return Math.ceil(damageRatio * this.designCost * 0.5);
  }

  needsRepair(): boolean {
    if (this.currentHp < this.totalHp) return true;
    return this.components.some(c =>
      (!c.ejected && c.broken) ||
      (!c.ejected && !c.broken && c.compCurrentHp < c.compMaxHp),
    );
  }

  healHp(amount: number): void {
    if (amount <= 0) return;
    let worst: InstalledComponent | null = null;
    let worstRatio = 1;
    for (const c of this.components) {
      if (c.broken || c.ejected || c.compMaxHp <= 0) continue;
      const ratio = c.compCurrentHp / c.compMaxHp;
      if (ratio < worstRatio) {
        worstRatio = ratio;
        worst = c;
      }
    }
    if (worst && worst.compCurrentHp < worst.compMaxHp) {
      worst.compCurrentHp = Math.min(worst.compMaxHp, worst.compCurrentHp + amount);
      this.damageSystem.updateCracks(worst);
      this.fx.updateCompSmoke(worst);
    }
    this.currentHp = Math.min(this.totalHp, this.currentHp + amount);
  }

  repairFull(): void {
    for (const comp of this.components) {
      if (comp.broken && !comp.ejected) {
        this.repairComponent(comp);
      }
      if (!comp.broken && !comp.ejected && comp.compCurrentHp < comp.compMaxHp) {
        comp.compCurrentHp = comp.compMaxHp;
        comp.crackLevel = 0;
        this.damageSystem.removeCracks(comp);
        this.fx.stopCompSmoke(comp);
      }
    }
    this.currentHp = this.totalHp;
    if (this.isFallen) this.standUp();
    this.recalcStats();
    this.updateDebugOverlays();
  }

  rebuildFull(): void {
    for (const comp of this.components) {
      if (comp.ejected) {
        comp.ejected = false;
        comp.broken = false;
        comp.compCurrentHp = comp.compMaxHp;
        comp.crackLevel = 0;
        comp.graphic = this.createComponentGraphic(comp.def, comp.localX, comp.localY, comp.rotation, comp.flipX, comp.flipY, comp.evoLevel);
        (comp as any)._baseScaleX = comp.graphic.scaleX;
        (comp as any)._baseScaleY = comp.graphic.scaleY;
        this.container.add(comp.graphic);
      } else if (comp.broken) {
        this.repairComponent(comp);
      } else if (comp.compCurrentHp < comp.compMaxHp) {
        comp.compCurrentHp = comp.compMaxHp;
        comp.crackLevel = 0;
        this.damageSystem.removeCracks(comp);
        this.fx.stopCompSmoke(comp);
      }
    }
    this.currentHp = this.totalHp;
    if (this.isFallen) this.standUp();
    this.recalcStats();
    this.updateDebugOverlays();
  }

  getRebuildCost(): number {
    const ejected = this.components.filter(c => c.ejected);
    if (ejected.length === 0) return 0;
    const ejectedCost = ejected.reduce((sum, c) => sum + c.def.cost, 0);
    return this.getRepairCost() + ejectedCost;
  }

  needsRebuild(): boolean {
    return this.components.some(c => c.ejected);
  }

  getBrokenComponents(): InstalledComponent[] {
    return this.components.filter(c => c.broken);
  }

  applyStun(duration: number): void {
    if (!this.alive) return;
    this.stunTimer = Math.max(this.stunTimer, duration);
  }

  stun(duration: number): void { this.applyStun(duration); }

  knockBack(_maxTiles: number, _proximity?: number): void { /* no-op for robots */ }

  applyBurn(_duration: number, _dps: number): void { /* no-op for robots */ }

  getVelocity(): { vx: number; vy: number } { return { vx: this.vx, vy: this.vy }; }

  applyPushback(fromX: number, fromY: number, strength: number): void {
    if (!this.alive) return;
    const dx = this.x - fromX;
    const dy = this.y - fromY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 0.1) return;
    const nx = dx / dist;
    const ny = dy / dist;
    this.vx += nx * strength;
    this.vy += ny * strength;
  }

  // ── Component queries ──

  getWeapons(): InstalledComponent[] {
    return this.components.filter(
      c => !c.broken && c.def.category === ComponentCategory.WEAPON && c.def.type !== ComponentType.EXPLOSIVE_CHARGE
    );
  }

  hasExplosiveCharge(): boolean {
    return this.components.some(c => !c.broken && c.def.type === ComponentType.EXPLOSIVE_CHARGE);
  }

  getExplosiveCharge(): InstalledComponent | null {
    return this.components.find(c => !c.broken && c.def.type === ComponentType.EXPLOSIVE_CHARGE) ?? null;
  }

  getSupportModules(): InstalledComponent[] {
    return this.components.filter(c => !c.broken && c.def.category === ComponentCategory.SUPPORT);
  }

  hasCamouflage(): boolean {
    return this.components.some(c => !c.broken && c.def.type === ComponentType.CAMOUFLAGE);
  }

  isSupportRobot(): boolean {
    return this.components.some(c => !c.broken && c.def.category === ComponentCategory.SUPPORT);
  }

  isIdle(): boolean {
    return this.state === RobotState.IDLE || this.state === RobotState.SUPPORTING;
  }

  canMove(): boolean {
    if (this.moveForce <= 0) return false;
    if (this.isHopping) return true;
    if (!this.wheelsAreLowest()) return false;
    return true;
  }

  /** @deprecated Use canFly() */
  hasWings(): boolean { return this.canFly(); }

  canFly(): boolean {
    return this.components.some(c =>
      !c.broken && (c.def.type === ComponentType.WING || c.def.type === ComponentType.TURBINE)
    );
  }

  get flightIntegrity(): number {
    const flightComps = this.components.filter(
      c => c.def.type === ComponentType.WING || c.def.type === ComponentType.TURBINE
    );
    if (flightComps.length === 0) return 0;
    const working = flightComps.filter(c => !c.broken).length;
    return working / flightComps.length;
  }

  // ── Jumper queries ──

  getBottomJumpers(): InstalledComponent[] {
    const jumpers = this.components.filter(c => !c.broken && c.def.type === ComponentType.JUMPER);
    if (jumpers.length === 0) return [];
    const maxY = Math.max(...this.components.filter(c => !c.broken).map(c => c.localY));
    return jumpers.filter(c => c.localY >= maxY - 1);
  }

  getSideJumpers(): InstalledComponent[] {
    const jumpers = this.components.filter(c => !c.broken && c.def.type === ComponentType.JUMPER);
    if (jumpers.length === 0) return [];
    const maxY = Math.max(...this.components.filter(c => !c.broken).map(c => c.localY));
    return jumpers.filter(c => c.localY < maxY - 1);
  }

  hasBottomJumper(): boolean { return this.getBottomJumpers().length > 0; }

  // ── Topple / Fallen state — delegated to TopplePhysics ──

  topple(fallDir: number): void {
    this.topplePhysics.topple(fallDir);
  }

  updateTopplePhysics(dtMs: number): boolean {
    return this.topplePhysics.updateTopplePhysics(dtMs);
  }

  standUp(): void {
    this.topplePhysics.standUp();
  }

  // ── Flying crash sequence — delegated to TopplePhysics ──

  startCrash(): void {
    this.topplePhysics.startCrash();
  }

  updateCrashPhysics(dt: number): boolean {
    return this.topplePhysics.updateCrashPhysics(dt);
  }

  // ── Camouflage visual effect ──

  updateCamouflage(dt: number): void {
    const hasCamo = this.hasCamouflage();
    const hasEnergy = this.currentEnergy > 0 || GOD.infiniteEnergy;
    const shouldCloak = hasCamo && hasEnergy && this.alive && !this.isFallen && !this.isCrashing;

    const wasCamo = this.isCamoActive;
    this.isCamoActive = shouldCloak;

    const fadeSpeed = 2.0;
    const scene = this.scene;

    if (shouldCloak) {
      this.camoTimer += dt;

      const targetAlpha = 0.45;
      this.camoAlpha = Math.max(targetAlpha, this.camoAlpha - fadeSpeed * dt);

      const flicker = 0.03 * Math.sin(this.camoTimer * 8);
      const glitch = Math.random() < 0.025 ? 0.12 : 0;
      this.container.setAlpha(this.camoAlpha + flicker + glitch);

      if (!wasCamo) {
        for (const comp of this.components) {
          if (comp.graphic && !comp.broken) {
            (comp.graphic as any).setTint?.(0x44cc88);
          }
        }
      }

      if (Math.random() < 0.05 && scene) {
        const sr = this.physicsRadius;
        const lineY = this.y - sr + Math.random() * sr * 2;
        const line = scene.add.rectangle(this.x, lineY, sr * 2, 1, 0x44ffaa, 0.3)
          .setDepth(this.container.depth + 1).setBlendMode(Phaser.BlendModes.ADD);
        scene.tweens.add({
          targets: line, alpha: 0, scaleY: 0.2,
          duration: 150 + Math.random() * 100,
          onComplete: () => line.destroy(),
        });
      }

      if (Math.random() < 0.06 && scene) {
        const angle = Math.random() * Math.PI * 2;
        const r = this.physicsRadius * (0.5 + Math.random() * 0.5);
        const sp = scene.add.text(
          this.x + Math.cos(angle) * r,
          this.y + Math.sin(angle) * r,
          '\u2B21', { fontSize: '5px', color: '#44cc88' },
        ).setOrigin(0.5).setAlpha(0.5).setDepth(this.container.depth + 1)
          .setBlendMode(Phaser.BlendModes.ADD);
        scene.tweens.add({
          targets: sp, alpha: 0, scale: 2,
          duration: 300 + Math.random() * 200,
          onComplete: () => sp.destroy(),
        });
      }
    } else {
      if (this.camoAlpha < 1) {
        this.camoAlpha = Math.min(1, this.camoAlpha + fadeSpeed * dt);
        this.container.setAlpha(this.camoAlpha);

        if (this.camoAlpha >= 1) {
          for (const comp of this.components) {
            if (comp.graphic && !comp.broken) {
              (comp.graphic as any).clearTint?.();
            }
          }
        }
      }

      if (wasCamo && scene) {
        const pr = this.physicsRadius;
        const ring = scene.add.circle(this.x, this.y, 2, 0x44ffaa, 0)
          .setStrokeStyle(2, 0x44ffaa, 0.6).setDepth(this.container.depth + 2)
          .setBlendMode(Phaser.BlendModes.ADD);
        scene.tweens.add({
          targets: ring,
          scaleX: pr * 0.12, scaleY: pr * 0.12, alpha: 0,
          duration: 350, ease: 'Quad.easeOut',
          onComplete: () => ring.destroy(),
        });
      }

      this.camoTimer = 0;
    }
  }

  updateSpeedTrail(dt: number): void {
    if (!this.alive || !this.container?.active) return;
    const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
    const threshold = this.speed * 0.6;
    if (speed < threshold || speed < 30) return;

    this.trailTimer += dt;
    const interval = 0.06;
    if (this.trailTimer < interval) return;
    this.trailTimer = 0;

    const invAngle = Math.atan2(-this.vy, -this.vx);
    const offset = this.physicsRadius * 0.7;
    const px = this.x + Math.cos(invAngle) * offset + (Math.random() - 0.5) * 6;
    const py = this.y + Math.sin(invAngle) * offset + (Math.random() - 0.5) * 6;

    const intensity = Math.min((speed - threshold) / (this.speed * 0.4), 1);
    const size = 2 + intensity * 3;
    const alpha = 0.3 + intensity * 0.3;
    const color = this.canFly() ? 0x88bbff : 0xccaa77;

    const dust = this.scene.add.circle(px, py, size, color, alpha).setDepth(4);
    this.scene.tweens.add({
      targets: dust,
      scaleX: 1.8 + intensity, scaleY: 1.8 + intensity,
      alpha: 0,
      x: px + Math.cos(invAngle) * (8 + intensity * 12),
      y: py + Math.sin(invAngle) * (8 + intensity * 12),
      duration: 250 + Math.random() * 150,
      ease: 'Quad.easeOut',
      onComplete: () => dust.destroy(),
    });
  }

  hasStabLegs(): boolean {
    return this.components.some(c => !c.broken && c.def.type === ComponentType.STAB_LEGS);
  }

  wheelsAreLowest(): boolean {
    const wheels = this.components.filter(c => c.def.type === ComponentType.WHEEL);
    const otherComps = this.components.filter(
      c => c.def.type !== ComponentType.WHEEL && c.def.type !== ComponentType.WING
        && c.def.type !== ComponentType.THRUSTER && c.def.type !== ComponentType.TURBINE
    );
    if (wheels.length === 0) return true;
    if (otherComps.length === 0) return true;
    const lowestOtherY = Math.max(...otherComps.map(c => c.localY));
    const highestWheelY = Math.min(...wheels.map(c => c.localY));
    return highestWheelY >= lowestOtherY;
  }

  // ── Position ──

  setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.container.setPosition(x, y);
  }

  distanceTo(x: number, y: number): number {
    const dx = this.x - x;
    const dy = this.y - y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  static readonly SWEEP_MEMORY_DURATION = 3;

  isInSensorCone(ex: number, ey: number, range: number, enemyId?: string): boolean {
    const dx = ex - this.x;
    const dy = ey - this.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > range * range) return false;

    if (!this.hasDirectionComponent) return true;

    const angleToTarget = Math.atan2(dy, dx);
    let inCone = false;

    if (this.sensorDirections.length <= 1) {
      const sweepHeading = this.heading + this.sweepAngle;
      let diff = angleToTarget - sweepHeading;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      inCone = Math.abs(diff) <= this.sensorHalfAngle;
    } else {
      for (const dir of this.sensorDirections) {
        const coneHeading = this.heading + dir;
        let diff = angleToTarget - coneHeading;
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;
        if (Math.abs(diff) <= this.sensorHalfAngle) {
          inCone = true;
          break;
        }
      }
    }

    if (inCone && enemyId) {
      const wasKnown = this.sweepMemory.has(enemyId);
      this.sweepMemory.set(enemyId, Date.now());
      if (!wasKnown) {
        SoundManager.play('radarPing');
      }
    }

    if (inCone) return true;

    if (enemyId) {
      const lastSeen = this.sweepMemory.get(enemyId);
      if (lastSeen !== undefined) {
        const elapsed = (Date.now() - lastSeen) / 1000;
        if (elapsed < Robot.SWEEP_MEMORY_DURATION) return true;
        this.sweepMemory.delete(enemyId);
      }
    }

    return false;
  }

  pruneSweepMemory(): void {
    const now = Date.now();
    const maxAge = Robot.SWEEP_MEMORY_DURATION * 1000;
    for (const [id, ts] of this.sweepMemory) {
      if (now - ts > maxAge) this.sweepMemory.delete(id);
    }
  }

  // ── Graphics ──

  createComponentGraphic(def: ComponentDef, lx: number, ly: number, rotation: number = 0, flipX = false, flipY = false, evoLevel?: number): Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite {
    const visual = createComponentVisual(this.scene, def.type, COMPONENT_SPACING, def.color, evoLevel);
    const level = evoLevel ?? ProgressionManager.getComponentLevel(def.type);
    const ov = getOverride(def.type, level);
    const defRot = ov?.defaultRotation ?? def.defaultRotation;
    const totalRotation = rotation + defRot;
    const offScale = COMPONENT_SPACING / 90;
    const cos = Math.cos(totalRotation);
    const sin = Math.sin(totalRotation);
    const rawOx = (ov?.offsetX ?? def.offsetX) * offScale;
    const rawOy = (ov?.offsetY ?? def.offsetY) * offScale;
    const ox = rawOx * cos - rawOy * sin;
    const oy = rawOx * sin + rawOy * cos;
    visual.setPosition(lx + ox, ly + oy);
    if (totalRotation !== 0) visual.setRotation(totalRotation);
    if (flipX) visual.scaleX *= -1;
    if (flipY) visual.scaleY *= -1;
    // Track wheel/turbine visuals via FX module
    this.fx.trackComponentVisual({ def } as InstalledComponent, visual as Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite);
    return visual as Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite;
  }

  public normalizeSize(): void {
    if (this.components.length === 0) { this.renderScale = 1; return; }
    this.renderScale = Robot.SCALE_ROBOT / COMPONENT_SPACING;
    if (this.canFly()) this.renderScale *= Robot.FLY_SCALE_BOOST;
    const sx = this.flipped ? -this.renderScale : this.renderScale;
    this.container.setScale(sx, this.renderScale);
    this.applyComponentSpacing();
    this.updatePhysicsRadius();
    this.updateFlyShadowExistence();
    if (!this.canFly() && !this.groundShadow) {
      const shadowKey = '_robot_ground_shadow';
      if (!this.scene.textures.exists(shadowKey)) {
        const sw = 40; const sh = 14;
        const rt = makeRenderTexture(this.scene, sw, sh);
        const gfx = this.scene.make.graphics({});
        gfx.fillStyle(0x000000, 0.25);
        gfx.fillEllipse(sw / 2, sh / 2, sw, sh);
        rt.draw(gfx, 0, 0);
        rt.saveTexture(shadowKey);
        rt.destroy();
        gfx.destroy();
      }
      const halfW = this.physicsRadius * 1.0;
      const halfH = this.physicsRadius * 0.35;
      this.groundShadow = this.scene.add.sprite(this.x, this.y + this.physicsRadius * 0.5, shadowKey)
        .setDisplaySize(halfW * 2, halfH * 2)
        .setDepth(2);
    } else if (this.canFly() && this.groundShadow) {
      this.groundShadow.destroy();
      this.groundShadow = undefined;
    }
  }

  // ── Flying shadow ──

  private updateFlyShadowExistence(): void {
    if (this.canFly() && !this.flyShadow) {
      const shadowKey = '_robot_fly_shadow';
      if (!this.scene.textures.exists(shadowKey)) {
        const sw = 64; const sh = 32;
        const rt = makeRenderTexture(this.scene, sw, sh);
        const gfx = this.scene.make.graphics({});
        gfx.fillStyle(0x000000, 1.0);
        gfx.fillEllipse(sw / 2, sh / 2, sw, sh);
        rt.draw(gfx, 0, 0);
        rt.saveTexture(shadowKey);
        rt.destroy();
        gfx.destroy();
      }
      this.flyShadow = this.scene.add.sprite(0, 0, shadowKey);
      this.flyShadow.setDepth(2);
      this.flyShadow.setAlpha(0.35);
    } else if (!this.canFly() && this.flyShadow) {
      this.flyShadow.destroy();
      this.flyShadow = undefined;
    }
  }

  // Cached fly shadow dimensions
  private flyShadowW = 0;
  private flyShadowH = 0;
  private flyShadowDirty = true;
  private lastFlyShadowX = -9999;
  private lastFlyShadowY = -9999;

  invalidateFlyShadow(): void { this.flyShadowDirty = true; }

  updateFlyShadow(): void {
    if (!this.flyShadow || !this.alive) {
      this.flyShadow?.setVisible(false);
      return;
    }

    const xSnap = Math.round(this.x);
    const ySnap = Math.round(this.y);
    this.lastFlyShadowX = xSnap;
    this.lastFlyShadowY = ySnap;

    if (this.flyShadowDirty) {
      this.flyShadowDirty = false;
      const sm = GOD.componentSpacingMult;
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      let anyActive = false;
      for (const c of this.components) {
        if (c.broken) continue;
        anyActive = true;
        minX = Math.min(minX, c.localX * sm);
        maxX = Math.max(maxX, c.localX * sm);
        minY = Math.min(minY, c.localY * sm);
        maxY = Math.max(maxY, c.localY * sm);
      }
      if (!anyActive) { this.flyShadow.setVisible(false); return; }
      const halfW = ((maxX - minX) / 2 + COMPONENT_SPACING * 0.5) * this.renderScale;
      const halfH = ((maxY - minY) / 2 + COMPONENT_SPACING * 0.5) * this.renderScale;
      this.flyShadowW = halfW * 1.4;
      this.flyShadowH = halfH * 0.6;
    }

    this.flyShadow.setVisible(true);

    const t = this.scene.time.now / 1000;
    const wave = Math.sin(t * GOD.flyAltitudeSpeed + this.flyAltitudePhase);
    const altExtra = wave * GOD.flyAltitudeShadowAmp;

    const sx = this.x + Robot.SHADOW_OFFSET_X + altExtra * 0.5;
    const sy = this.y + Robot.SHADOW_OFFSET_Y + altExtra;
    this.flyShadow.setPosition(sx, sy);
    this.flyShadow.setDisplaySize(this.flyShadowW * 2, this.flyShadowH * 2);

    const altScale = 1 + wave * GOD.flyAltitudeScaleAmp;
    const base = this.renderScale * altScale;
    const flipX = this.flipped ? -base : base;
    this.container.setScale(flipX, base);

    this.flyShadow.setAlpha(0.35 - wave * 0.1);
  }

  private applyComponentSpacing(): void {
    const sm = GOD.componentSpacingMult;
    const offScale = COMPONENT_SPACING / 90;
    for (const c of this.components) {
      if (c.graphic) {
        const cos = Math.cos(c.rotation);
        const sin = Math.sin(c.rotation);
        const rawOx = c.def.offsetX * offScale;
        const rawOy = c.def.offsetY * offScale;
        const ox = rawOx * cos - rawOy * sin;
        const oy = rawOx * sin + rawOy * cos;
        c.graphic.setPosition(c.localX * sm + ox, c.localY * sm + oy);
      }
    }
  }

  private updatePhysicsRadius(): void {
    const sm = GOD.componentSpacingMult;
    let maxExtent = 0;
    const halfComp = COMPONENT_SPACING / 2;
    for (const c of this.components) {
      maxExtent = Math.max(maxExtent, Math.abs(c.localX * sm) + halfComp, Math.abs(c.localY * sm) + halfComp);
    }
    this.physicsRadius = maxExtent * this.renderScale;
  }

  public getExposedTargetPosition(): { x: number; y: number } | null {
    const exposed = this.components.filter(c => c.exposed && !c.broken && c.compMaxHp > 0);
    if (exposed.length === 0) return null;

    const priority = (c: InstalledComponent): number => {
      switch (c.def.category) {
        case ComponentCategory.WEAPON: return 4;
        case ComponentCategory.SENSOR: return 3;
        case ComponentCategory.SUPPORT: return 2;
        default: return 1;
      }
    };

    exposed.sort((a, b) => priority(b) - priority(a));
    const topPriority = priority(exposed[0]);
    const topTier = exposed.filter(c => priority(c) === topPriority);
    const target = topTier[Math.floor(Math.random() * topTier.length)];

    return this.getWeaponWorldPos(target);
  }

  public getWeaponWorldPos(comp: InstalledComponent): { x: number; y: number } {
    const s = this.renderScale;
    const sm = GOD.componentSpacingMult;
    const lx = (this.flipped ? -comp.localX : comp.localX) * sm * s;
    const ly = comp.localY * sm * s;
    const rot = this.container.rotation;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    return {
      x: this.x + lx * cos - ly * sin,
      y: this.y + lx * sin + ly * cos,
    };
  }

  // ── FX delegation ──

  public updateWheelSpin(): void { this.fx.updateWheelSpin(); }
  public updateTurbineSpin(): void { this.fx.updateTurbineSpin(); }

  public createDebugOverlays(): void {
    // Sensor range circle
    this.sensorCircle = this.scene.add.circle(0, 0, 0, 0x44cc44, 0.06);
    this.sensorCircle.setStrokeStyle(2, 0x44cc44, 0.5);
    this.sensorCircle.setDepth(19);
    this.sensorCircle.setVisible(false);

    // Sensor cone graphics
    this.sensorConeGfx = this.scene.add.graphics();
    this.sensorConeGfx.setDepth(19);
    this.sensorConeGfx.setVisible(false);

    // HP + Energy bars via BarRenderer
    this.barRenderer.createBars();
  }

  private bringOverlaysToTop(): void {
    // Overlays are now scene-level, no container reorder needed
  }

  /** Pause all infinite component tweens */
  pauseComponentTweens(): void {
    for (const comp of this.components) {
      if (comp.tween && comp.tween.isPlaying()) comp.tween.pause();
    }
  }

  /** Resume all infinite component tweens */
  resumeComponentTweens(): void {
    for (const comp of this.components) {
      if (comp.tween && !comp.broken) comp.tween.resume();
    }
  }

  /**
   * Check current tile under robot and update terrain modifier.
   */
  updateTerrain(currentTile: TileType | null): void {
    if (!this.alive || currentTile === this.lastTerrainTile) return;
    this.lastTerrainTile = currentTile;

    if (this.hasWings()) {
      if (this.terrainMod !== null) {
        this.terrainMod = null;
        this.terrainLabel?.destroy();
        this.terrainLabel = undefined;
      }
      return;
    }

    const mod = currentTile !== null ? (TERRAIN_MODIFIERS[currentTile] ?? null) : null;
    const changed = mod !== this.terrainMod;
    this.terrainMod = mod;

    if (changed && mod && mod.label) {
      this.terrainLabel?.destroy();
      const colorHex = '#' + mod.color.toString(16).padStart(6, '0');
      this.terrainLabel = this.scene.add.text(this.x, this.y - 30, mod.label, {
        fontSize: '10px', color: colorHex, fontFamily: 'monospace', fontStyle: 'bold',
        stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(50);
      this.scene.tweens.add({
        targets: this.terrainLabel,
        y: this.y - 50, alpha: 0,
        duration: 1800, ease: 'Power2',
        onComplete: () => { this.terrainLabel?.destroy(); this.terrainLabel = undefined; },
      });
    }
  }

  get terrainFireRateMult(): number {
    return this.terrainMod?.fireRateMult ?? 1.0;
  }

  updateDebugOverlays(): void {
    if (!this.alive) return;

    // Viewport culling
    const cam = this.scene.cameras.main;
    const margin = 60;
    const onScreen = this.x >= cam.worldView.x - margin
      && this.x <= cam.worldView.right + margin
      && this.y >= cam.worldView.y - margin
      && this.y <= cam.worldView.bottom + margin;
    if (!onScreen) {
      this.barRenderer.hideAll();
      this.sensorCircle?.setVisible(false);
      this.sensorConeGfx?.setVisible(false);
      this.groundShadow?.setVisible(false);
      this.flyShadow?.setVisible(false);
      this.container?.setVisible(false);
      return;
    }
    this.container?.setVisible(true);
    this.barRenderer.showBgs();
    this.groundShadow?.setVisible(true);

    // Flying shadow
    this.updateFlyShadow();

    // Ground shadow follows robot
    if (this.groundShadow) {
      this.groundShadow.setPosition(this.x, this.y + this.physicsRadius * 0.5);
    }

    // Pause/resume component animations when paralyzed
    if (this.energySaveMode && !this.fx.compAnimsPaused) {
      this.fx.compAnimsPaused = true;
      for (const comp of this.components) {
        if (comp.tween) comp.tween.pause();
      }
    } else if (!this.energySaveMode && this.fx.compAnimsPaused) {
      this.fx.compAnimsPaused = false;
      for (const comp of this.components) {
        if (comp.tween && !comp.broken) comp.tween.resume();
      }
    }

    // Sync crack overlays with their parent component
    for (const comp of this.components) {
      if (!comp.crackOverlay || !comp.graphic) continue;
      comp.crackOverlay.setPosition(comp.graphic.x, comp.graphic.y);
      comp.crackOverlay.setRotation(comp.graphic.rotation);
      comp.crackOverlay.setAlpha(comp.graphic.alpha);
      comp.crackOverlay.setScale(comp.graphic.scaleX, comp.graphic.scaleY);
    }

    // Start component animations once
    if (this.placed && !this.fx.compAnimsStarted) {
      this.fx.startComponentAnims();
    }

    // Live-update GOD stat multipliers
    if (this.baseTotalHp > 0) {
      let newTotal = 0;
      for (const c of this.components) {
        if (c.broken) continue;
        const scaledMax = Math.round(c.def.hp * GOD.robotHpMult);
        if (scaledMax !== c.compMaxHp) {
          const compRatio = c.compMaxHp > 0 ? c.compCurrentHp / c.compMaxHp : 1;
          c.compMaxHp = scaledMax;
          c.compCurrentHp = Math.round(scaledMax * compRatio);
        }
        newTotal += scaledMax;
      }
      if (newTotal !== this.totalHp) {
        const ratio = this.totalHp > 0 ? this.currentHp / this.totalHp : 1;
        this.totalHp = newTotal;
        this.currentHp = Math.round(newTotal * ratio);
      }
    }
    if (this.baseMaxEnergy > 0) {
      const newEn = Math.round(this.baseMaxEnergy * GOD.robotEnergyMult);
      if (newEn !== this.maxEnergy) {
        const ratio = this.maxEnergy > 0 ? this.currentEnergy / this.maxEnergy : 1;
        this.maxEnergy = newEn;
        this.currentEnergy = Math.round(newEn * ratio);
      }
    }
    if (this.baseSpeed > 0) {
      this.speed = this.baseSpeed * GOD.robotSpeedMult;
    }

    // Live-update render scale
    if (this.components.length > 0) {
      const newScale = Robot.SCALE_ROBOT / COMPONENT_SPACING;
      if (Math.abs(newScale - this.renderScale) > 0.001) {
        this.renderScale = newScale;
        const sx = this.flipped ? -newScale : newScale;
        this.container.setScale(sx, newScale);
        this.applyComponentSpacing();
        this.updatePhysicsRadius();
      }
    }

    // Sensor overlay
    const showSensor = this.sensorRange > 0 && this.showSensorCircle;
    if (this.hasDirectionComponent && this.sensorConeGfx) {
      if (this.sensorCircle) this.sensorCircle.setVisible(false);
      if (showSensor) {
        const xSnap = Math.round(this.x);
        const ySnap = Math.round(this.y);
        const hSnap = Math.round(this.heading * 100);
        const sSnap = Math.round(this.sweepAngle * 100);
        const coneDirty = xSnap !== this.lastConeX || ySnap !== this.lastConeY
          || hSnap !== this.lastConeHeading || sSnap !== this.lastConeSweep;
        if (!coneDirty) {
          this.sensorConeGfx.setVisible(true);
        } else {
        this.lastConeX = xSnap;
        this.lastConeY = ySnap;
        this.lastConeHeading = hSnap;
        this.lastConeSweep = sSnap;
        this.sensorConeGfx.clear();

        if (this.sensorDirections.length <= 1) {
          const sweepDir = this.heading + this.sweepAngle;
          const trailSteps = 6;
          const trailArc = Math.PI * 0.6;
          for (let i = trailSteps; i >= 1; i--) {
            const frac = i / trailSteps;
            const trailAngle = sweepDir - frac * trailArc;
            const alpha = 0.03 * (1 - frac);
            if (alpha < 0.002) continue;
            this.sensorConeGfx.fillStyle(0x44cc44, alpha);
            this.sensorConeGfx.beginPath();
            this.sensorConeGfx.moveTo(this.x, this.y);
            this.sensorConeGfx.arc(this.x, this.y, this.sensorRange,
              trailAngle - this.sensorHalfAngle, trailAngle + this.sensorHalfAngle, false);
            this.sensorConeGfx.closePath();
            this.sensorConeGfx.fillPath();
          }
          this.sensorConeGfx.fillStyle(0x44cc44, 0.08);
          this.sensorConeGfx.lineStyle(2, 0x44cc44, 0.5);
          this.sensorConeGfx.beginPath();
          this.sensorConeGfx.moveTo(this.x, this.y);
          this.sensorConeGfx.arc(this.x, this.y, this.sensorRange,
            sweepDir - this.sensorHalfAngle, sweepDir + this.sensorHalfAngle, false);
          this.sensorConeGfx.closePath();
          this.sensorConeGfx.fillPath();
          this.sensorConeGfx.strokePath();
          this.sensorConeGfx.lineStyle(1.5, 0x88ff88, 0.6);
          this.sensorConeGfx.beginPath();
          this.sensorConeGfx.moveTo(this.x, this.y);
          this.sensorConeGfx.lineTo(
            this.x + Math.cos(sweepDir) * this.sensorRange,
            this.y + Math.sin(sweepDir) * this.sensorRange,
          );
          this.sensorConeGfx.strokePath();
        } else {
          const tints = [0x44cc44, 0x44aacc, 0xccaa44, 0xcc44aa, 0xaa44cc, 0x44ccaa];
          for (let i = 0; i < this.sensorDirections.length; i++) {
            const dir = this.heading + this.sensorDirections[i];
            const tint = tints[i % tints.length];
            this.sensorConeGfx.fillStyle(tint, 0.06);
            this.sensorConeGfx.lineStyle(1.5, tint, 0.4);
            this.sensorConeGfx.beginPath();
            this.sensorConeGfx.moveTo(this.x, this.y);
            this.sensorConeGfx.arc(this.x, this.y, this.sensorRange,
              dir - this.sensorHalfAngle, dir + this.sensorHalfAngle, false);
            this.sensorConeGfx.closePath();
            this.sensorConeGfx.fillPath();
            this.sensorConeGfx.strokePath();
            this.sensorConeGfx.lineStyle(1, tint, 0.5);
            this.sensorConeGfx.beginPath();
            this.sensorConeGfx.moveTo(this.x, this.y);
            this.sensorConeGfx.lineTo(
              this.x + Math.cos(dir) * this.sensorRange * 0.7,
              this.y + Math.sin(dir) * this.sensorRange * 0.7,
            );
            this.sensorConeGfx.strokePath();
          }
        }

        this.sensorConeGfx.setVisible(true);
        } // end coneDirty
      } else {
        this.sensorConeGfx.setVisible(false);
        this.lastConeX = -9999;
      }
    } else {
      if (this.sensorConeGfx) this.sensorConeGfx.setVisible(false);
      if (this.sensorCircle) {
        if (showSensor) {
          this.sensorCircle.setPosition(this.x, this.y);
          this.sensorCircle.setRadius(this.sensorRange);
          this.sensorCircle.setVisible(true);
        } else {
          this.sensorCircle.setVisible(false);
        }
      }
    }

    // Bar rendering — delegated to BarRenderer
    this.barRenderer.updateBars();
  }

  /** Invalidate cached bar state */
  public invalidateBars(): void {
    this.barRenderer.invalidateBars();
  }

  /** Destroy scene-level HP/energy bars */
  public destroyBars(): void {
    this.barRenderer.destroyBars();
    this.sensorCircle?.destroy();
    this.sensorConeGfx?.destroy();
  }

  // ── Lifecycle ──

  destroy(): void {
    this.alive = false;
    this.state = RobotState.DESTROYED;

    // Stop all live component smoke timers
    this.fx.stopAllCompSmoke();
    // Remove all crack overlays
    for (const comp of this.components) this.damageSystem.removeCracks(comp);

    // Death explosion
    this.playDeathExplosion();

    // Clean up event listeners
    this.fx.destroyListeners();

    this.container.destroy();
    this.flyShadow?.destroy();
    this.flyShadow = undefined;
    this.groundShadow?.destroy();
    this.groundShadow = undefined;
    this.destroyBars();
    this.sensorCircle?.destroy();
    this.sensorConeGfx?.destroy();
    this.stunStars?.destroy();
    this.stunStars = null;
    this.sweepMemory.clear();
    EventBus.emit(GameEvents.ROBOT_DESTROYED, this);
  }

  /**
   * Death explosion with simulated physics.
   */
  private playDeathExplosion(): void {
    const scene = this.scene;
    const s = this.renderScale;
    const rot = this.container.rotation;
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);
    const flipX = this.flipped ? -1 : 1;

    if (scene.textures.exists('fx_explosion')) {
      const exp = scene.add.image(this.x, this.y, 'fx_explosion').setDepth(30);
      exp.setScale(0.3);
      scene.tweens.add({
        targets: exp, scaleX: 0.8 + Math.random() * 0.4, scaleY: 0.8 + Math.random() * 0.4,
        alpha: 0, duration: 250 + Math.random() * 200, ease: 'Power2',
        onComplete: () => exp.destroy(),
      });
    }

    if (scene.textures.exists('fx_spark')) {
      const spark = scene.add.image(this.x, this.y, 'fx_spark').setDepth(31);
      spark.setScale(0.4);
      scene.tweens.add({
        targets: spark, scaleX: 0.8 + Math.random() * 0.5, scaleY: 0.8 + Math.random() * 0.5,
        alpha: 0, duration: 150 + Math.random() * 100,
        onComplete: () => spark.destroy(),
      });
    }

    const GRAVITY = 350 + Math.random() * 150;
    const GROUND_Y = this.y + 16 + Math.random() * 8;
    const BOUNCE_DAMPING = 0.3 + Math.random() * 0.2;
    const MAX_LIFETIME = 1.8;
    const DEBRIS_SCALE = s * (0.7 + Math.random() * 0.2);
    /** Smoke threshold for debris trail */
    const SMOKE_THRESHOLD = 0.7;

    interface DebrisState {
      go: Phaser.GameObjects.GameObject & { setPosition: Function; setRotation: Function; setScale: Function; setAlpha: Function; destroy: Function; x: number; y: number };
      vx: number; vy: number; spin: number;
      age: number; bounces: number;
      scaleVal: number;
      smoking: boolean;
      smokeAccum: number;
      smokeInterval: number;
    }

    const allDebris: DebrisState[] = [];

    for (const comp of this.components) {
      const sm = GOD.componentSpacingMult;
      const lx = (flipX * comp.localX) * sm * s;
      const ly = comp.localY * sm * s;
      const worldX = this.x + lx * cosR - ly * sinR;
      const worldY = this.y + lx * sinR + ly * cosR;

      const compSize = COMPONENT_SPACING;
      const debris = createComponentVisual(scene, comp.def.type, compSize, comp.def.color, comp.evoLevel);
      debris.setPosition(worldX, worldY);
      const internalScale = (debris as any).scaleX as number ?? 1;
      const finalScale = internalScale * DEBRIS_SCALE;
      debris.setScale(finalScale);
      (debris as Phaser.GameObjects.Graphics).setDepth(29);
      debris.setRotation(rot);

      const dx = worldX - this.x;
      const dy = worldY - this.y;
      const baseDist = Math.sqrt(dx * dx + dy * dy);
      const outAngle = baseDist > 1
        ? Math.atan2(dy, dx) + (Math.random() - 0.5) * 1.2
        : Math.random() * Math.PI * 2;

      const speed = 80 + Math.random() * 120;
      const vx = Math.cos(outAngle) * speed;
      const vy = -Math.abs(Math.sin(outAngle) * speed) - (60 + Math.random() * 80);

      const hpRatio = comp.compMaxHp > 0 ? comp.compCurrentHp / comp.compMaxHp : 1;
      const isSmoking = hpRatio < SMOKE_THRESHOLD;
      const smokeInt = comp.broken ? 80 : (hpRatio < 0.4 ? 140 : 250);

      allDebris.push({
        go: debris as DebrisState['go'],
        vx, vy,
        spin: (Math.random() - 0.5) * 12,
        age: 0,
        bounces: 0,
        scaleVal: finalScale,
        smoking: isSmoking,
        smokeAccum: 0,
        smokeInterval: smokeInt,
      });
    }

    const hasSmokeTex = scene.textures.exists('fx_smoke');

    const STEP = 16;
    const timer = scene.time.addEvent({
      delay: STEP,
      loop: true,
      callback: () => {
        const dt = STEP / 1000;
        let allDone = true;

        for (const d of allDebris) {
          if (d.age >= MAX_LIFETIME) continue;
          allDone = false;
          d.age += dt;

          d.vy += GRAVITY * dt;

          const nx = d.go.x + d.vx * dt;
          const ny = d.go.y + d.vy * dt;
          d.go.setPosition(nx, ny);

          d.go.setRotation((d.go as any).rotation + d.spin * dt);

          if (d.smoking && hasSmokeTex) {
            d.smokeAccum += STEP;
            if (d.smokeAccum >= d.smokeInterval) {
              d.smokeAccum -= d.smokeInterval;
              const puff = scene.add.image(nx, ny, 'fx_smoke').setDepth(24);
              const ps = 0.06 + Math.random() * 0.08;
              puff.setScale(ps);
              puff.setAlpha(0.25 + Math.random() * 0.15);
              puff.setTint(0x777777);
              scene.tweens.add({
                targets: puff,
                x: nx + (Math.random() - 0.5) * 4,
                y: ny - 6 - Math.random() * 8,
                scaleX: ps * 2.5, scaleY: ps * 2.5,
                alpha: 0,
                duration: 300 + Math.random() * 200,
                onComplete: () => puff.destroy(),
              });
            }
          }

          if (ny >= GROUND_Y && d.vy > 0) {
            d.go.setPosition(nx, GROUND_Y);
            d.vy = -d.vy * BOUNCE_DAMPING;
            d.vx *= (0.6 + Math.random() * 0.2);
            d.spin *= 0.5;
            d.bounces++;

            if (d.bounces >= 3 || Math.abs(d.vy) < 15) {
              d.vy = 0;
              d.vx *= 0.3;
            }
          }

          if (d.bounces > 0) {
            d.scaleVal *= (1 - dt * 0.4);
            d.go.setScale(d.scaleVal);
          }

          const fadeStart = MAX_LIFETIME * 0.7;
          if (d.age > fadeStart) {
            const fadeT = 1 - (d.age - fadeStart) / (MAX_LIFETIME - fadeStart);
            d.go.setAlpha(Math.max(0, fadeT));
          }

          if (d.age >= MAX_LIFETIME) {
            d.go.destroy();
          }
        }

        if (allDone) timer.destroy();
      },
    });

    if (scene.textures.exists('fx_smoke')) {
      const puffCount = 2 + Math.floor(Math.random() * 4);
      for (let i = 0; i < puffCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.random() * 15;
        const sx = this.x + Math.cos(angle) * dist;
        const sy = this.y + Math.sin(angle) * dist;
        const smoke = scene.add.image(sx, sy, 'fx_smoke').setDepth(28);
        smoke.setScale(0.15 + Math.random() * 0.25);
        smoke.setAlpha(0.4 + Math.random() * 0.2);
        scene.tweens.add({
          targets: smoke,
          x: sx + Math.cos(angle) * (15 + Math.random() * 20),
          y: sy - 10 - Math.random() * 15,
          scaleX: 0.8 + Math.random() * 0.6,
          scaleY: 0.8 + Math.random() * 0.6,
          alpha: 0,
          duration: 400 + Math.random() * 300,
          delay: Math.random() * 150,
          onComplete: () => smoke.destroy(),
        });
      }
    }
  }
}
